import type { AddressSuggestionFields } from '~~/layers/base/utils/crane/address'
import type { AddressSuggestionOverlayMode } from '~~/layers/base/utils/crane/addressVerifyPolicy'
import type { AddressCardAddresseeContext } from '~~/layers/base/utils/formatAddressLine'

export type { AddressSuggestionOverlayMode } from '~~/layers/base/utils/crane/addressVerifyPolicy'

/** Explicit card pick vs swipe/X/overlay dismiss (dismiss aborts the in-flight save). */
export type AddressSuggestionChoice =
    | { status: 'picked'; address: AddressSuggestionFields }
    | { status: 'cancelled' }

/**
 * Suggested-address picker. Layout-mounted AdaptiveDrawer (TK-12977) instead of Nuxt
 * `useOverlay` (double scrim). Dismiss without tapping a card resolves `cancelled` so the
 * parent save gate can return to the address form/drawer without persisting.
 */
export type AddressSuggestionOverlayOptions = {
    mode?: AddressSuggestionOverlayMode
}

const drawerOpen = ref(false)
const original = ref<AddressSuggestionFields>({})
const suggested = ref<AddressSuggestionFields>({})
const addresseeContext = ref<AddressCardAddresseeContext | undefined>()
const mode = ref<AddressSuggestionOverlayMode>('confirm')

let settle: ((value: AddressSuggestionChoice) => void) | null = null

function resolvePending(choice: AddressSuggestionChoice) {
    const resolve = settle
    settle = null
    drawerOpen.value = false
    resolve?.(choice)
}

export function useAddressSuggestionOverlay() {
    async function confirmAddressSuggestion(
        nextOriginal: AddressSuggestionFields,
        nextSuggested: AddressSuggestionFields,
        nextAddresseeContext?: AddressCardAddresseeContext,
        options?: AddressSuggestionOverlayOptions
    ): Promise<AddressSuggestionChoice> {
        if (!import.meta.client) {
            return { status: 'picked', address: nextOriginal }
        }
        if (settle) {
            resolvePending({ status: 'cancelled' })
        }
        original.value = nextOriginal
        suggested.value = nextSuggested
        addresseeContext.value = nextAddresseeContext
        mode.value = options?.mode ?? 'confirm'
        drawerOpen.value = true
        return new Promise((resolve) => {
            settle = resolve
        })
    }

    function closePicked(address: AddressSuggestionFields) {
        resolvePending({ status: 'picked', address })
    }

    function closeCancelled() {
        resolvePending({ status: 'cancelled' })
    }

    return {
        drawerOpen,
        original,
        suggested,
        addresseeContext,
        mode,
        confirmAddressSuggestion,
        closePicked,
        closeCancelled,
    }
}
